import React from 'react';

import { getCrmFieldIdentityKey } from '@aline/common';
import { Typography } from '@mui/material';
import { isDev, isStaging } from 'src/config';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useGetCurrentDoc } from 'src/hooks';
import { createClampStyles } from 'src/utils/styles';
import { makeStyles } from 'tss-react/mui';

import type { DocumentCRMField } from './hooks';

const EMPTY_VALUE = '—';

const FIELD_TYPE_LABEL: Record<Apollo.IntegrationFieldType, string> = {
  [Apollo.IntegrationFieldType.Array]: 'List',
  [Apollo.IntegrationFieldType.Boolean]: 'True/false',
  [Apollo.IntegrationFieldType.Number]: 'Number',
  [Apollo.IntegrationFieldType.String]: 'Text',
};

const VARIANT_LABEL: Record<Apollo.IntegrationFieldVariant, string> = {
  [Apollo.IntegrationFieldVariant.Native]: 'Native',
  [Apollo.IntegrationFieldVariant.Unified]: 'Unified',
};

const VALUE_STATUS_LABEL: Record<Apollo.IntegrationFieldValueStatus, string> = {
  [Apollo.IntegrationFieldValueStatus.Available]: 'Available',
  [Apollo.IntegrationFieldValueStatus.Unavailable]: 'Unavailable',
};

interface DebugRowDef {
  getValue: (integrationField: DocumentCRMField) => string;
  label: string;
}

/**
 * Rows that only mean something to Aline engineers debugging a mapping (Slate node ids,
 * catalog variants, raw paths). Hidden from customers — see `useShowsFieldDebugRows`.
 */
const DEBUG_ROWS: DebugRowDef[] = [
  { getValue: field => field.path, label: 'Path' },
  { getValue: field => field.fieldInDocInfo?.nodeId ?? EMPTY_VALUE, label: 'Node ID' },
  { getValue: field => field.recordId ?? EMPTY_VALUE, label: 'Record ID' },
  { getValue: field => VARIANT_LABEL[field.variant] ?? EMPTY_VALUE, label: 'Variant' },
  {
    getValue: field => VALUE_STATUS_LABEL[field.valueStatus] ?? EMPTY_VALUE,
    label: 'Value status',
  },
  {
    getValue: field => {
      if (!field.fieldInDocInfo) return EMPTY_VALUE;
      return field.fieldInDocInfo.isStale ? 'Yes' : 'No';
    },
    label: 'Stale',
  },
];

const useTooltipStyles = makeStyles()(theme => ({
  debug: {
    borderTop: `1px dashed ${theme.appColors.rule}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    marginTop: theme.spacing(0.25),
    paddingTop: theme.spacing(0.75),
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.25, 1.5),
    textAlign: 'left',
    width: 240,
  },
  row: {
    alignItems: 'baseline',
    display: 'flex',
    gap: theme.spacing(1.5),
    justifyContent: 'space-between',
  },
  rowValue: {
    minWidth: 0,
    overflowWrap: 'anywhere',
    textAlign: 'right',
  },
  rows: {
    borderTop: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    paddingTop: theme.spacing(0.75),
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  // Long text fields (descriptions, addresses) would otherwise stretch the card.
  value: {
    ...createClampStyles(3),
  },
  valueMissing: {
    fontStyle: 'italic',
  },
}));

interface IntegrationFieldTooltipContentProps {
  integration: Apollo.Integration;
  integrationField: DocumentCRMField;
}

/** Details card shown from the info icon on an integration field: value, data type and API name. */
export function IntegrationFieldTooltipContent({
  integration,
  integrationField,
}: IntegrationFieldTooltipContentProps): JSX.Element {
  const { classes } = useTooltipStyles();
  const showsDebugRows = useShowsFieldDebugRows();
  const fieldTypeLabel =
    FIELD_TYPE_LABEL[integrationField.type] ?? FIELD_TYPE_LABEL[Apollo.IntegrationFieldType.String];

  return (
    <div className={classes.root}>
      <Typography variant="subtitle2">{integrationField.title}</Typography>
      <div className={classes.rows}>
        <IntegrationFieldTooltipRow label={`Value in ${integration}`}>
          <IntegrationFieldTooltipValue integrationField={integrationField} />
        </IntegrationFieldTooltipRow>
        <IntegrationFieldTooltipTextRow label="Type" value={fieldTypeLabel} />
        <IntegrationFieldTooltipTextRow
          label="Custom"
          value={integrationField.isCustom ? 'Yes' : 'No'}
        />
        <IntegrationFieldTooltipTextRow
          label="Field"
          value={getCrmFieldIdentityKey(integration)(integrationField.path)}
        />
      </div>
      {showsDebugRows && (
        <div className={classes.debug}>
          <Typography className={classes.sectionLabel} color="textSecondary" variant="caption">
            Aline debug
          </Typography>
          {DEBUG_ROWS.map(({ getValue, label }) => (
            <IntegrationFieldTooltipTextRow
              key={label}
              label={label}
              value={getValue(integrationField)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function IntegrationFieldTooltipValue({
  integrationField,
}: {
  integrationField: DocumentCRMField;
}): JSX.Element {
  const { classes } = useTooltipStyles();
  const { document } = useGetCurrentDoc();

  if (integrationField.valueStatus === Apollo.IntegrationFieldValueStatus.Unavailable)
    return (
      <Typography color="error" variant="caption">
        Could not be read
      </Typography>
    );

  // Templates are not linked to a CRM record, so there is no value to read yet.
  if (document?.variant === Apollo.DocumentVariant.Template)
    return (
      <Typography className={classes.valueMissing} color="textSecondary" variant="caption">
        Fills in at launch
      </Typography>
    );

  const value = integrationField.value?.trim();
  if (!value)
    return (
      <Typography className={classes.valueMissing} color="textSecondary" variant="caption">
        Empty
      </Typography>
    );

  return (
    <Typography className={classes.value} variant="caption">
      {value}
    </Typography>
  );
}

function IntegrationFieldTooltipTextRow({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element {
  return (
    <IntegrationFieldTooltipRow label={label}>
      <Typography variant="caption">{value}</Typography>
    </IntegrationFieldTooltipRow>
  );
}

function IntegrationFieldTooltipRow({
  children,
  label,
}: {
  children: React.ReactNode;
  label: string;
}): JSX.Element {
  const { classes } = useTooltipStyles();

  return (
    <div className={classes.row}>
      <Typography noWrap color="textSecondary" variant="caption">
        {label}
      </Typography>
      <div className={classes.rowValue}>{children}</div>
    </div>
  );
}

/** Aline-internal viewers (local, staging, support) also see the raw mapping rows. */
function useShowsFieldDebugRows(): boolean {
  const currentUser = useSelectCurrentUser();
  return isDev || isStaging || Boolean(currentUser?.supportRole);
}
